// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

import type { Preset, PotraceOptions, TraceParams } from './types';

/** Clamp a number into [lo, hi]. */
export function clamp(n: number, lo: number, hi: number): number {
  if (Number.isNaN(n)) return lo;
  return Math.min(hi, Math.max(lo, n));
}

/**
 * The default turnpolicy (4 = "minority") and posterization algorithm (0 =
 * simple) are the sensible potrace defaults; we don't expose them in the UI.
 */
export const TURN_POLICY = 4;
export const POSTERIZE_SIMPLE = 0;

/** Translate friendly TraceParams into the raw options esm-potrace-wasm wants. */
export function toPotraceOptions(p: TraceParams): PotraceOptions {
  return {
    turdsize: Math.round(clamp(p.speckle, 0, 100)),
    turnpolicy: TURN_POLICY,
    alphamax: clamp(p.corner, 0, 1.334),
    opticurve: 1,
    opttolerance: clamp(p.optimize, 0.05, 2),
    pathonly: false,
    extractcolors: p.color,
    // Black & white is a single level; colour uses the requested detail level.
    posterizelevel: p.color ? Math.round(clamp(p.levels, 2, 32)) : 2,
    posterizationalgorithm: POSTERIZE_SIMPLE,
  };
}

export const PRESETS: Preset[] = [
  {
    id: 'photo',
    label: 'Photo',
    group: 'Colour',
    hint: 'Rich colour — best for photographs and detailed artwork.',
    params: { color: true, levels: 8, speckle: 4, corner: 1, optimize: 0.2, maxDim: 1000 },
  },
  {
    id: 'logo',
    label: 'Logo / flat',
    group: 'Colour',
    hint: 'Clean flat-colour tracing — best for logos, icons and illustrations.',
    params: { color: true, levels: 5, speckle: 8, corner: 1, optimize: 0.2, maxDim: 1200 },
  },
  {
    id: 'poster',
    label: 'Poster',
    group: 'Colour',
    hint: 'Bold, few colours, chunky shapes — a screen-print look.',
    params: { color: true, levels: 3, speckle: 14, corner: 1, optimize: 0.3, maxDim: 1000 },
  },
  {
    id: 'lineart',
    label: 'Line art',
    group: 'Black & white',
    hint: 'Smooth single-colour outlines — best for logos, stamps and inked art.',
    params: { color: false, levels: 2, speckle: 2, corner: 1, optimize: 0.2, maxDim: 1400 },
  },
  {
    id: 'sketch',
    label: 'Sketch',
    group: 'Black & white',
    hint: 'Detailed black & white — keeps fine strokes and texture.',
    params: { color: false, levels: 2, speckle: 0, corner: 1, optimize: 0.1, maxDim: 1600 },
  },
];

export const DEFAULT_PRESET_ID = 'logo';

export function presetById(id: string): Preset | undefined {
  return PRESETS.find((p) => p.id === id);
}

/** Deep-copy a preset's params so edits don't mutate the constant. */
export function paramsFromPreset(id: string): TraceParams {
  const p = presetById(id) ?? PRESETS[0];
  return { ...p.params };
}

/**
 * Decide whether the current params still match a named preset exactly, or have
 * diverged into "Custom". Returns the matching preset id, or null for custom.
 */
export function matchPreset(params: TraceParams): string | null {
  for (const p of PRESETS) {
    const a = p.params;
    if (
      a.color === params.color &&
      a.levels === params.levels &&
      a.speckle === params.speckle &&
      a.corner === params.corner &&
      a.optimize === params.optimize &&
      a.maxDim === params.maxDim
    ) {
      return p.id;
    }
  }
  return null;
}
